import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { PublicShell } from "@/components/layouts/PublicShell";
import { Seo } from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Check, Zap, Wrench, Sparkles } from "lucide-react";
import { Price } from "@/components/pricing/Price";
import { startPlanCheckout } from "@/lib/payments/checkoutSession";
import { ProviderHint } from "@/components/payments/ProviderHint";
import { useEntitlements } from "@/hooks/useEntitlements";
import {
  CONSUMER_PLANS,
  PLAN_ENTITLEMENTS,
  formatFCFA,
  annualSavingsPct,
  type ConsumerTierCode,
} from "@/config/entitlements";
import { toast } from "sonner";
import { PRICING } from "@/config/pricing";
import { useSkuEurPrices } from "@/hooks/useSkuPrices";

type Interval = "monthly" | "annual";
type CurrencyDisplay = "EUR" | "FCFA";

// FCFA is mobile-money territory → no trial; bill first month immediately.
const MOBILE_MONEY_CURRENCIES = new Set<CurrencyDisplay>(["FCFA"]);

export default function Pricing() {
  const { t } = useTranslation();
  const ent = useEntitlements();
  const { prices: skuPrices } = useSkuEurPrices();
  const skuCents = (sku: string, fallback: number) => skuPrices[sku] ?? fallback;
  const [searchParams] = useSearchParams();
  const highlightTier = (() => {
    const raw = searchParams.get("highlight");
    if (raw && ["free", "founder", "operator", "team"].includes(raw)) {
      return raw as ConsumerTierCode;
    }
    return null;
  })();
  useEffect(() => {
    if (!highlightTier) return;
    const el = document.querySelector<HTMLElement>(`[data-tier-card="${highlightTier}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightTier]);
  const [interval, setInterval] = useState<Interval>("monthly");
  const [currency, setCurrency] = useState<CurrencyDisplay>("EUR");
  const [coachingTier, setCoachingTier] = useState<"4h" | "8h" | "12h">("8h");

  const showFCFA = currency === "FCFA";
  const isCardRail = !MOBILE_MONEY_CURRENCIES.has(currency);

  const trialCta = t("marketing.pricing.ctaTrial");
  const firstMonthCta = t("marketing.pricing.ctaFirstMonth");
  const freeCta = t("marketing.pricing.ctaFree");

  const formatPrice = (cents: number, suffix?: string) => {
    if (cents === 0) {
      if (showFCFA) {
        return (
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold tracking-tight text-text-1">0 FCFA</span>
            {suffix && <span className="text-sm text-text-3">{suffix}</span>}
          </div>
        );
      }
      return <Price size="card" eurCents={0} />;
    }
    if (showFCFA) {
      return (
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold tracking-tight text-text-1">{formatFCFA(cents)}</span>
          {suffix && <span className="text-sm text-text-3">{suffix}</span>}
        </div>
      );
    }
    return <Price size="card" eurCents={cents} suffix={suffix} />;
  };

  const onCheckout = (planId: string) => {
    if (!ent.isAuthed) {
      toast.info(t("marketing.pricing.signInFirst"));
    }
    const providerOverride =
      typeof window !== "undefined" && sessionStorage.getItem("preferred_provider") === "flutterwave"
        ? ("flutterwave" as const)
        : undefined;
    startPlanCheckout(planId, { providerOverride }).catch((e) => {
      toast.error(
        t("payments.errors.unknown") +
          ": " +
          (e as Error).message,
      );
    });
  };

  const onChooseFallback = () => {
    sessionStorage.setItem("preferred_provider", "flutterwave");
    toast.success(
      t("payments.wave.fallbackChosen"),
    );
  };

  // ---- Tier cards ----
  type TierCardSpec = {
    code: ConsumerTierCode;
    title: string;
    description: string;
    highlight?: boolean;
    badge?: string;
  };

  const tierCards: TierCardSpec[] = [
    {
      code: "free",
      title: t("marketing.pricing.tierFree"),
      description: t("marketing.pricing.freeDesc2"),
    },
    {
      code: "founder",
      title: t("marketing.pricing.tierFounder"),
      description: t("marketing.pricing.founderDesc2"),
      highlight: true,
      badge: t("marketing.pricing.popular"),
    },
    {
      code: "operator",
      title: t("marketing.pricing.tierOperator"),
      description: t("marketing.pricing.operatorDesc"),
    },
    {
      code: "team",
      title: t("marketing.pricing.tierTeam"),
      description: t("marketing.pricing.teamDesc"),
    },
  ];

  const featureLines = (tier: ConsumerTierCode): string[] => {
    const e = PLAN_ENTITLEMENTS[tier];
    const lines: string[] = [];
    lines.push(
      e.koraDailyMessageLimit === null
        ? t("marketing.pricing.feat.koraUnlimited")
        : t("marketing.pricing.feat.koraLimit", { n: e.koraDailyMessageLimit }),
    );
    if (e.velyraEnabled) {
      lines.push(
        t("marketing.pricing.feat.velyra"),
      );
    }
    lines.push(
      e.selfPacedLibrary === "full"
        ? t("marketing.pricing.feat.libraryFull")
        : t("marketing.pricing.feat.librarySamples"),
    );
    lines.push(
      e.community === "full"
        ? t("marketing.pricing.feat.communityFull")
        : t("marketing.pricing.feat.communityRead"),
    );
    if (e.officeHours === "facilitated_group") {
      lines.push(
        t("marketing.pricing.feat.officeFacilitated"),
      );
    } else if (e.officeHours === "group") {
      lines.push(t("marketing.pricing.feat.officeGroup"));
    }
    if (e.recordings) {
      lines.push(t("marketing.pricing.feat.recordings"));
    }
    if (e.certificates) {
      lines.push(t("marketing.pricing.feat.certs"));
    }
    lines.push(
      e.resourceLibrary === "unlimited"
        ? t("marketing.pricing.feat.resourcesAll")
        : t("marketing.pricing.feat.resources3"),
    );
    if (e.premiumPlaybooks) {
      lines.push(t("marketing.pricing.feat.playbooks"));
    }
    if (e.platformDiscountPct > 0) {
      lines.push(
        t("marketing.pricing.feat.discount", { pct: e.platformDiscountPct }),
      );
    }
    if (e.seats > 1) {
      lines.push(
        t("marketing.pricing.feat.seats", { n: e.seats }),
      );
    }
    return lines;
  };

  const planIdFor = (code: ConsumerTierCode, iv: Interval): string | null => {
    if (code === "free") return null;
    return `${code}.${iv}`;
  };

  const ctaFor = (code: ConsumerTierCode): string => {
    if (code === "free") return freeCta;
    return isCardRail ? trialCta : firstMonthCta;
  };

  const priceFor = (code: ConsumerTierCode): { cents: number; suffix: string } => {
    const plan = CONSUMER_PLANS[code];
    const cents = interval === "annual" ? plan.annual : plan.monthly;
    const suffix =
      cents === 0
        ? ""
        : interval === "annual"
        ? t("marketing.pricing.perYear")
        : t("marketing.pricing.perMonth");
    return { cents, suffix };
  };

  const founderSavings = annualSavingsPct("founder");

  // ---- One-off products ----
  const bootcampTiers = [
    { key: "beg", name: t("marketing.pricing.bootcampBeginner"), priceCents: skuCents("bootcamp-beginner", PRICING.bootcamp.beginner_eur_cents), planId: "bootcamp.beginner" },
    { key: "int", name: t("marketing.pricing.bootcampIntermediate"), priceCents: skuCents("bootcamp-intermediate", PRICING.bootcamp.intermediate_eur_cents), planId: "bootcamp.intermediate", highlight: true },
    { key: "adv", name: t("marketing.pricing.bootcampAdvanced"), priceCents: skuCents("bootcamp-advanced", PRICING.bootcamp.advanced_eur_cents), planId: "bootcamp.advanced" },
  ];

  // For display: real discount is applied server-side at checkout via resolvePrice().
  const operatorDiscountPct = PLAN_ENTITLEMENTS.operator.platformDiscountPct;

  const microservicePriceCents = skuCents("microservice-base", PRICING.microservice.from_eur_cents);

  const coachingPlans = {
    "4h":  { priceCents: skuCents("coaching-4h",  PRICING.coaching.tier_4h_eur_cents),  hoursLabel: t("marketing.pricing.coaching.4h") },
    "8h":  { priceCents: skuCents("coaching-8h",  PRICING.coaching.tier_8h_eur_cents),  hoursLabel: t("marketing.pricing.coaching.8h") },
    "12h": { priceCents: skuCents("coaching-12h", PRICING.coaching.tier_12h_eur_cents), hoursLabel: t("marketing.pricing.coaching.12h") },
  } as const;

  const faq = useMemo(
    () => [
      {
        q: t("marketing.pricing.faq.trialQ"),
        a: t("marketing.pricing.faq.trialA"),
      },
      {
        q: t("marketing.pricing.faq.discountQ"),
        a: t("marketing.pricing.faq.discountA"),
      },
      {
        q: t("marketing.pricing.faq.mobileMoneyQ"),
        a: t("marketing.pricing.faq.mobileMoneyA2"),
      },
      {
        q: t("marketing.pricing.faq.refundsQ"),
        a: t("marketing.pricing.faq.refundsA"),
        href: "/legal/refund-policy",
      },
    ],
    [t],
  );

  return (
    <PublicShell>
      <Seo
        title={t("marketing.pricing.seoTitle")}
        description={t("marketing.pricing.seoDescription")}
      />

      {/* Toggles (the page <h1> hero is rendered once by PublicShell's PageBanner) */}
      <section className="border-b border-border-1">
        <div className="container py-16 md:py-20">
          <div className="flex flex-col items-center gap-4">
            {/* Monthly / Annual */}
            <div className="inline-flex items-center rounded-full border border-border-1 bg-surface-2 p-1 text-sm">
              <button
                onClick={() => setInterval("monthly")}
                className={`rounded-full px-4 py-1.5 font-medium transition ${
                  interval === "monthly" ? "bg-primary text-primary-foreground" : "text-text-3 hover:text-text-1"
                }`}
              >
                {t("marketing.pricing.monthly")}
              </button>
              <button
                onClick={() => setInterval("annual")}
                className={`rounded-full px-4 py-1.5 font-medium transition ${
                  interval === "annual" ? "bg-primary text-primary-foreground" : "text-text-3 hover:text-text-1"
                }`}
              >
                {t("marketing.pricing.annual")}
                <span className="ml-1.5 rounded-full bg-success/15 px-1.5 py-0.5 text-[10px] font-semibold text-success">
                  {t("marketing.pricing.savePct", { pct: founderSavings })}
                </span>
              </button>
            </div>

            {/* Currency */}
            <div className="inline-flex items-center rounded-full border border-border-1 bg-surface-2 p-1 text-xs">
              {(["EUR", "FCFA"] as const).map((c) => (
                <button
                  key={c}
                  onClick={() => setCurrency(c)}
                  className={`rounded-full px-3 py-1 font-medium transition ${
                    currency === c ? "bg-primary text-primary-foreground" : "text-text-3 hover:text-text-1"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            {showFCFA && (
              <p className="text-xs text-text-3">
                {t("marketing.pricing.fcfaNote")}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* 4-tier grid */}
      <section className="border-b border-border-1">
        <div className="container py-16">
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {tierCards.map((card) => {
              const { cents, suffix } = priceFor(card.code);
              const pid = planIdFor(card.code, interval);
              return (
                <Card
                  key={card.code}
                  variant={card.highlight ? "feature" : "interactive"}
                  data-tier-card={card.code}
                  className={`relative overflow-hidden ${
                    highlightTier === card.code
                      ? "ring-2 ring-primary ring-offset-2 ring-offset-background"
                      : ""
                  }`}
                >
                  {card.badge && (
                    <div className="absolute right-0 top-0">
                      <div className="bg-primary px-4 py-1 t-eyebrow text-primary-foreground shadow-md">
                        {card.badge}
                      </div>
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle className="text-xl">{card.title}</CardTitle>
                    <div className="mt-3">{formatPrice(cents, cents === 0 ? undefined : suffix)}</div>
                    <CardDescription className="mt-2">{card.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ul className="space-y-2">
                      {featureLines(card.code).map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2.25} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    {card.code === "free" ? (
                      <Button asChild className="w-full" variant="brandOutline" size="lg">
                        <Link to="/diagnostic">{ctaFor(card.code)} →</Link>
                      </Button>
                    ) : (
                      <Button
                        className="w-full"
                        variant={card.highlight ? "brand" : "brandOutline"}
                        size={card.highlight ? "xl" : "lg"}
                        onClick={() => pid && onCheckout(pid)}
                      >
                        {ctaFor(card.code)}
                      </Button>
                    )}
                    {card.code !== "free" && showFCFA && (
                      <ProviderHint onChooseFallback={onChooseFallback} />
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <p className="mt-8 text-center text-sm text-text-3">
            {isCardRail
              ? t("marketing.pricing.trialNote")
              : t("marketing.pricing.firstMonthNote")}
          </p>
        </div>
      </section>

      {/* Section — Bootcamps & cohorts */}
      <section className="border-b border-border-1 bg-surface-2/40">
        <div className="container py-16">
          <h2 className="mb-2 text-center t-display-sm text-text-1">
            {t("marketing.pricing.s2Title")}
          </h2>
          <p className="mb-8 text-center t-body-sm text-text-3">
            {t("marketing.pricing.s2Sub2")}
          </p>
          <div className="grid gap-6 md:grid-cols-3">
            {bootcampTiers.map((tier) => (
              <Card
                key={tier.key}
                variant={tier.highlight ? "feature" : "interactive"}
                className="relative overflow-hidden"
              >
                {tier.highlight && (
                  <div className="absolute right-0 top-0">
                    <div className="bg-primary px-4 py-1 t-eyebrow text-primary-foreground shadow-md">
                      {t("marketing.pricing.popular")}
                    </div>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-xl">
                    {t("marketing.pricing.bootcampPrefix")} — {tier.name}
                  </CardTitle>
                  <div className="mt-2">
                    {formatPrice(
                      tier.priceCents,
                      t("marketing.pricing.perCohort"),
                    )}
                  </div>
                  <Badge variant="outline" className="mt-3 w-fit">
                    {t("marketing.pricing.discountEligible")}
                  </Badge>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button
                    className="w-full"
                    variant={tier.highlight ? "brand" : "brandOutline"}
                    size={tier.highlight ? "xl" : "lg"}
                    onClick={() => onCheckout(tier.planId)}
                  >
                    {t("marketing.pricing.ctaEnroll")} →
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Section — Microservices & Coaching */}
      <section className="border-b border-border-1">
        <div className="container py-16">
          <h2 className="mb-8 text-center t-display-sm text-text-1">
            {t("marketing.pricing.s3Title")}
          </h2>
          <div className="grid gap-6 md:grid-cols-2">
            {/* Microservices */}
            <Card variant="interactive" className="relative">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Wrench className="h-5 w-5 text-primary" />
                  <CardTitle className="text-xl">
                    {t("marketing.pricing.microTitle")}
                  </CardTitle>
                </div>
                <div className="mt-3">
                  {formatPrice(
                    microservicePriceCents,
                    t("marketing.pricing.fixedScope"),
                  )}
                </div>
                {ent.isAuthed && (
                  <p className="mt-2 text-xs text-success">
                    {t("marketing.pricing.subscriberPriceHint", { pct: operatorDiscountPct })}
                  </p>
                )}
                <CardDescription className="mt-2">
                  {t("marketing.pricing.microDesc2")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild className="w-full" variant="brandOutline" size="lg">
                  <Link to="/services/microservices">
                    {t("marketing.pricing.ctaBrowseMicro")} →
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Coaching */}
            <Card variant="interactive" className="relative">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-primary" />
                  <CardTitle className="text-xl">
                    {t("marketing.pricing.coachingTitle")}
                  </CardTitle>
                </div>
                <div className="mt-3 inline-flex items-center rounded-full border border-border-1 bg-surface-2 p-1 text-xs">
                  {(["4h", "8h", "12h"] as const).map((k) => (
                    <button
                      key={k}
                      onClick={() => setCoachingTier(k)}
                      className={`rounded-full px-3 py-1 font-medium transition ${
                        coachingTier === k ? "bg-primary text-primary-foreground" : "text-text-3 hover:text-text-1"
                      }`}
                    >
                      {k}/mo
                    </button>
                  ))}
                </div>
                <div className="mt-3">
                  {formatPrice(
                    coachingPlans[coachingTier].priceCents,
                    t("marketing.pricing.perMonth"),
                  )}
                </div>
                <CardDescription className="mt-2">
                  {coachingPlans[coachingTier].hoursLabel} ·{" "}
                  {t("marketing.pricing.coachingDesc2")}
                </CardDescription>
                <p className="mt-2 text-xs text-text-3">
                  {t("marketing.pricing.coachingNoDiscount")}
                </p>
              </CardHeader>
              <CardContent>
                <Button asChild className="w-full" variant="brandOutline" size="lg">
                  <Link to="/mentors">
                    {t("marketing.pricing.ctaFindCoach")} →
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section — Institutional / Enterprise (Contact only) */}
      <section className="border-b border-border-1 bg-gradient-to-br from-primary/10 to-surface-1">
        <div className="container py-16">
          <Card variant="feature" className="mx-auto max-w-5xl">
            <CardContent className="grid gap-6 p-8 md:grid-cols-[1.2fr_1fr] md:items-center">
              <div>
                <span className="mb-3 inline-flex items-center gap-2 t-eyebrow text-primary">
                  <Sparkles className="h-4 w-4" />
                  {t("marketing.pricing.enterpriseEyebrow")}
                </span>
                <h2 className="t-display-sm text-text-1">
                  {t("marketing.pricing.enterpriseTitle")}
                </h2>
                <p className="mt-2 text-text-3">
                  {t("marketing.pricing.enterpriseSub")}
                </p>
              </div>
              <div className="flex flex-col items-start gap-4 md:items-end">
                <Button asChild size="xl" variant="brand">
                  <Link to="/enterprise">
                    {t("marketing.pricing.ctaTalkSales")} →
                  </Link>
                </Button>
                <p className="text-xs text-text-3 md:text-right">
                  {t("marketing.pricing.enterpriseNoSelfServe")}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section>
        <div className="container py-16">
          <h2 className="mb-8 text-center t-display-md text-text-1">
            {t("marketing.pricing.faqTitle")}
          </h2>
          <Accordion type="single" collapsible className="mx-auto max-w-3xl">
            {faq.map((f, i) => (
              <AccordionItem key={i} value={`q-${i}`}>
                <AccordionTrigger className="text-left t-body font-semibold text-text-1">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent>
                  {f.a}
                  {(f as { href?: string }).href && (
                    <>
                      {" "}
                      <Link
                        to={(f as { href: string }).href}
                        className="font-medium text-primary hover:underline"
                      >
                        {t("marketing.pricing.faq.readFullPolicy")}
                      </Link>
                    </>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </PublicShell>
  );
}
